<template>
	<div
		v-if="filledBars > 0"
		v-tooltip="tooltipBinding"
		class="loudness-indicator"
	>
		<span
			v-for="barIndex in 5"
			:key="barIndex"
			class="loudness-indicator__bar"
			:class="{ '-filled': barIndex <= filledBars }"
			:style="{ height: `${4 + (barIndex * 2)}px` }"
		/>
	</div>
</template>

<script>
const clampScore = (score) => Math.max(0, Math.min(1, Number(score) || 0));

const escapeHtml = (value) => String(value)
	.replace(/&/g, '&amp;')
	.replace(/</g, '&lt;')
	.replace(/>/g, '&gt;')
	.replace(/"/g, '&quot;');

const formatLabel = (label) => String(label).replace(/_/g, ' ');

const formatScore = (score) => {
	const normalizedScore = clampScore(score);
	return Number.isInteger(normalizedScore)
		? String(normalizedScore)
		: normalizedScore.toFixed(2).replace(/\.?0+$/, '');
};

export default {
	name:  'LoudnessIndicator',
	props: {
		value: {
			type:    Number,
			default: null,
		},
		predictions: {
			type:    Array,
			default: () => [],
		},
		iqScore: {
			type:    Object,
			default: null,
		},
		tagData: {
			type:    Object,
			default: null,
		},
	},
	computed: {
		filledBars() {
			const audioScore = Number(this.value);
			if (!Number.isFinite(audioScore) || audioScore <= 0) {
				return 0;
			}

			// audioScore is on a 0–10 scale; show at least 2 bars when > 0 so low values (e.g. 0.4) stay visible.
			return Math.max(2, Math.round(Math.min(audioScore, 10) / 2));
		},
		resolvedPredictions() {
			if (this.predictions?.length) {
				return this.predictions;
			}

			return this.iqScore?.predictions ?? this.tagData?.predictions ?? [];
		},
		sceneLabels() {
			const labels = [];

			this.resolvedPredictions.forEach((prediction) => {
				if (!prediction || typeof prediction.label !== 'string') {
					return;
				}

				labels.push({
					label: prediction.label,
					score: clampScore(prediction.score),
				});
			});

			return labels.sort((left, right) => right.score - left.score);
		},
		intensityLabel() {
			const audioScore = Number(this.value);
			if (!Number.isFinite(audioScore) || audioScore <= 0) {
				return '';
			}

			const formattedScore = Number.isInteger(audioScore)
				? String(audioScore)
				: audioScore.toFixed(1);

			return `Intensity: ${formattedScore}/10`;
		},
		tooltipHtml() {
			const parts = [];

			if (this.intensityLabel) {
				parts.push(
					`<div class="loudness-indicator-tooltip__intensity">${escapeHtml(this.intensityLabel)}</div>`,
				);
			}

			if (this.sceneLabels.length) {
				if (this.intensityLabel) {
					parts.push('<div class="loudness-indicator-tooltip__divider" role="separator"></div>');
				}

				const rows = this.sceneLabels.map(({ label, score }) => (
					`<span class="loudness-indicator-tooltip__label" style="opacity: ${score}">${escapeHtml(formatLabel(label))} (${formatScore(score)})</span>`
				)).join('');

				parts.push(`<div class="loudness-indicator-tooltip__labels">${rows}</div>`);
			}

			if (!parts.length) {
				return '';
			}

			return `<div class="loudness-indicator-tooltip">${parts.join('')}</div>`;
		},
		tooltipBinding() {
			if (!this.tooltipHtml) {
				return null;
			}

			return {
				content: this.tooltipHtml,
				classes: 'tooltip s-html -sm',
				html:    true,
			};
		},
	},
};
</script>

<style lang="scss" scoped>
.loudness-indicator {
	display: inline-flex;
	align-items: flex-end;
	gap: 2px;
	flex-shrink: 0;
}

.loudness-indicator__bar {
	width: 2px;
	border-radius: 1px;
	background-color: var(--color-grey-700);

	&.-filled {
		background-color: var(--color-soft-white);
	}
}
</style>
<style lang="scss">
.loudness-indicator-tooltip {
	display: flex;
	flex-direction: column;
	gap: 4px;
	min-width: 80px;

	&__intensity {
		line-height: 1.3;
	}

	&__divider {
		height: 1px;
		margin: 2px 0;
		background-color: rgba(255, 255, 255, 0.25);
	}

	&__labels {
		display: flex;
		flex-direction: column;
		gap: 4px;
	}

	&__label {
		display: block;
		line-height: 1.3;
		text-transform: capitalize;
	}
}
</style>
